import { Checkmark, ChevronLeft, ChevronRight } from '@carbon/icons-react';
import type { SelectOption } from '@ph-react-ui/core';
import {
  Button,
  Table,
  Tag,
  Tooltip,
  useNotifications,
} from '@ph-react-ui/core';
import { useQueryClient, useQueries } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../../hooks/auth/useAuth';
import { useGetSchoolYearBaseSettings } from '../../../../hooks/settings/useGetSchoolYearBaseSettings';
import { fetchInstallmentsByStudent } from '../../../../hooks/taxes/school-taxes/parent/history/useGetInstallmentsByStudent';
import { useGetSchoolTaxes } from '../../../../hooks/taxes/school-taxes/useGetSchoolTaxes';
import { useModal } from '../../../../hooks/useModal';
import type { ScholarshipType } from '../../../../models/users/students/scholarship-type';
import { Scholarship } from '../../../../models/users/students/scholarship-type';
import { Select } from '../../../../utils/components/hoc-components';
import { PARTIAL_PAYMENT } from '../../../../utils/constants/taxes/modals';
import { displayPrice } from '../../../../utils/displayPrice';
import { ScopeAccess } from '../../../../utils/enums/scope-access.enum';
import { EmptyState } from '../../../shared/components/EmptyState';
import { TableSkeleton } from '../../../shared/components/skeletons/TableSkeleton';
import {
  getRowClassName,
  isPenaltyApplied,
  renderPenaltyCell,
} from './utils/schoolTaxesUtils';

export const ParentSchoolTaxes = () => {
  const { authState } = useAuth();
  const canMakePayments = authState?.scope?.includes(ScopeAccess.MakePayments);

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const { t } = useTranslation();
  const notification = useNotifications();

  const queryClient = useQueryClient();
  const { open: openPartialPaymentModal } = useModal(PARTIAL_PAYMENT);
  const methods = useForm();
  const [studentOptions, setStudentOptions] = useState<SelectOption<string>[]>(
    []
  );

  const { data: schoolYearSettings, isLoading: isLoadingSchoolYear } =
    useGetSchoolYearBaseSettings();

  const currentSchoolYear = schoolYearSettings?.startOfSchoolYear?.year ?? 0;
  const yearParam = searchParams.get('year');
  const selectedYear = yearParam ? Number(yearParam) : currentSchoolYear;

  const { data: pricePlanData, isLoading: isLoadingPricePlan } =
    useGetSchoolTaxes(selectedYear);

  const isYearNotReady = selectedYear <= 0 || isLoadingSchoolYear;

  const studentIdParam =
    searchParams.get('studentId') ?? studentOptions?.[0]?.value;

  useEffect(() => {
    if (!pricePlanData) return;

    const options = pricePlanData.map((student) => ({
      label: student.name,
      value: student.id,
    }));
    setStudentOptions(options);

    const hasSelectedStudent =
      Boolean(studentIdParam) &&
      options.some((student) => student.value === studentIdParam);

    if ((!studentIdParam || !hasSelectedStudent) && options.length > 0) {
      const updatedParams = new URLSearchParams(searchParams);
      updatedParams.set('studentId', options[0].value);
      setSearchParams(updatedParams);
    }
  }, [pricePlanData, studentIdParam, searchParams, setSearchParams]);

  useEffect(() => {
    if (currentSchoolYear && !yearParam) {
      searchParams.set('year', currentSchoolYear.toString());
      setSearchParams(searchParams);
    }
  }, [currentSchoolYear, yearParam, searchParams, setSearchParams]);

  const selectedStudent = useMemo(
    () => pricePlanData?.find((s) => s.id === studentIdParam),
    [pricePlanData, studentIdParam]
  );

  const onSelectStudent = useCallback(
    (value: string | number | (string | number)[] | null) => {
      if (value) {
        searchParams.set(
          'studentId',
          typeof value === 'string' ? value : value.toString()
        );
        setSearchParams(searchParams);
      }
    },
    [searchParams, setSearchParams]
  );

  const onNavigateToFullPayment = useCallback(() => {
    if (!selectedStudent) return;

    fetchInstallmentsByStudent(
      selectedStudent.id,
      selectedYear ?? undefined
    ).then((res) => {
      const remainingInstallmentsIds = res.result
        .filter((item) => !item.isPaid)
        .map((installment) => installment.id);

      queryClient.invalidateQueries({ queryKey: ['school-taxes-history'] });

      if (remainingInstallmentsIds.length) {
        const params = new URLSearchParams();
        params.set('studentId', selectedStudent.id);
        params.set('canPay', selectedStudent.canPay ? 'true' : 'false');
        if (selectedYear) {
          params.set('year', selectedYear.toString());
        }

        navigate(
          `pre-payment/${remainingInstallmentsIds.join('&')}/${
            selectedStudent.id
          }?${params.toString()}`
        );
      } else {
        notification.default(
          t('TAXES.MESSAGES.NO_SCHOOL_TAXES', { name: selectedStudent.name })
        );
      }
    });
  }, [selectedStudent, navigate, notification, queryClient, t, selectedYear]);

  const onOpenPartialPayment = useCallback(() => {
    if (!selectedStudent) return;
    openPartialPaymentModal({
      id: selectedStudent.id,
      name: selectedStudent.name,
    });
  }, [selectedStudent, openPartialPaymentModal]);

  const onPayInstallment = useCallback(
    (installmentId: string) => {
      if (!selectedStudent) return;

      const params = new URLSearchParams();
      params.set('studentId', selectedStudent.id);
      params.set('canPay', selectedStudent.canPay ? 'true' : 'false');
      if (selectedYear) {
        params.set('year', selectedYear.toString());
      }

      navigate(
        `pre-payment/${installmentId}/${
          selectedStudent.id
        }?${params.toString()}`
      );
    },
    [selectedStudent, navigate, selectedYear]
  );

  const onPrevYear = useCallback(() => {
    if (selectedYear && currentSchoolYear && selectedYear > currentSchoolYear) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('year', (selectedYear - 1).toString());
      setSearchParams(newParams);
    }
  }, [selectedYear, currentSchoolYear, searchParams, setSearchParams]);

  const onNextYear = useCallback(() => {
    if (
      selectedYear &&
      currentSchoolYear &&
      selectedYear === currentSchoolYear
    ) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('year', (selectedYear + 1).toString());
      setSearchParams(newParams);
    }
  }, [selectedYear, currentSchoolYear, searchParams, setSearchParams]);

  const { data: installmentsData, isLoading: isLoadingInstallments } =
    useQueries({
      queries: [
        {
          queryKey: ['installments', studentIdParam, selectedYear],
          queryFn: () =>
            studentIdParam
              ? fetchInstallmentsByStudent(studentIdParam, selectedYear)
              : null,
          enabled: !!studentIdParam && !!selectedYear,
          staleTime: 2 * 60 * 1000, // Cache for 2 minutes
          gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
        },
      ],
    })[0] || { data: null, isLoading: false };

  const canGoToNextYear = selectedYear === currentSchoolYear;
  const canGoToPrevYear =
    selectedYear && currentSchoolYear && selectedYear > currentSchoolYear;
  const isOnSchoolTaxesScholarship = (scholarshipType: ScholarshipType) =>
    scholarshipType !== Scholarship.NO_SCHOLARSHIP;

  const headers = useMemo(
    () => [
      t('PAYMENT_SCHEMES.DRAWER.LABELS.INSTALLMENT'),
      t('PAYMENTS.TABLE.HEADERS.PENDING_AMOUNT'),
      t('PAYMENTS.TABLE.LABELS.DUE_DATE'),
      t('PAYMENTS.TABLE.LABELS.PENALTY'),
      t('TAXES.TABLE.HEADERS.STATUS'),
      t('PAYMENTS.TABLE.HEADERS.ACTIONS'),
    ],
    [t]
  );

  const tableChildren = useMemo(() => {
    if (!installmentsData?.result) return [];

    const allInstallments = [...installmentsData.result].sort(
      (a, b) => a.index - b.index
    );
    const unpaidInstallments = allInstallments.filter((inst) => !inst.isPaid);
    const firstUnpaidIndexValue =
      unpaidInstallments.length > 0
        ? Math.min(...unpaidInstallments.map((inst) => inst.index))
        : null;

    const isFirstInstallmentPaid = allInstallments[0]?.isPaid ?? true;

    return allInstallments.map((installment) => {
      const isEnabled =
        !installment.isPaid && installment.index === firstUnpaidIndexValue;
      const isDisabled =
        !isEnabled || !canMakePayments || !selectedStudent?.canPay;
      const penaltyApplied = isFirstInstallmentPaid
        ? isPenaltyApplied(installment)
        : false;
      const rowClassName = getRowClassName(installment.isPaid, penaltyApplied);

      const tooltipMessage = installment.isPaid
        ? t('TAXES.MESSAGES.INSTALLMENT_ALREADY_PAID')
        : !selectedStudent?.canPay
        ? t('TAXES.MESSAGES.OVERDUE_PAYMENTS', {
            schoolYear:
              selectedYear === currentSchoolYear
                ? `${selectedYear + 1} - ${selectedYear + 2}`
                : `${selectedYear - 1} - ${selectedYear}`,
          })
        : !isEnabled && canMakePayments
        ? t('PAYMENTS.TABLE.LABELS.PAY_PREVIOUS_FIRST')
        : '';

      const payButton = (
        <Button
          variant="outlined"
          disabled={isDisabled}
          onClick={() => onPayInstallment(installment.id)}
        >
          {t('PAYMENTS.TABLE.ACTIONS.PAY')}
        </Button>
      );

      return [
        <span className={rowClassName}>
          {t('PAYMENT_SCHEMES.DRAWER.LABELS.INSTALLMENT')} #
          {installment.index + 1}
        </span>,
        <span className={rowClassName}>
          {displayPrice(
            installment.amount,
            t('PAYMENTS.LABELS.BGN'),
            t('PAYMENTS.LABELS.EUR'),
            installment.currency
          )}
        </span>,
        <span className={rowClassName}>
          {installment.dueDate
            ? format(new Date(installment.dueDate), 'dd.MM.yyyy')
            : '-'}
        </span>,
        renderPenaltyCell({ installment, t, penaltyApplied }),
        installment.isPaid ? (
          <Tag type="success">{t('TAXES.STATUS.PAID')}</Tag>
        ) : (
          <Tag type="accent">{t('TAXES.STATUS.PENDING')}</Tag>
        ),
        tooltipMessage ? (
          <Tooltip position="left" message={tooltipMessage}>
            {payButton}
          </Tooltip>
        ) : (
          payButton
        ),
      ];
    });
  }, [
    installmentsData?.result,
    canMakePayments,
    selectedStudent?.canPay,
    t,
    selectedYear,
    currentSchoolYear,
    onPayInstallment,
  ]);

  if (isYearNotReady) {
    return <TableSkeleton />;
  }

  return (
    <FormProvider {...methods}>
      <div className="payments">
        <h2 className="payments__item__titles__h2 mb-8 mt-4">
          {t('PAYMENTS.TITLES.SCHOOL_TAXES')}
        </h2>
        <div className="school-taxes__actions">
          <Select
            name="student"
            compact
            placeholder={t('TAXES.LABELS.CHOOSE_STUDENT') ?? ''}
            label={t('TAXES.LABELS.CHOOSE_STUDENT') ?? ''}
            options={studentOptions}
            value={studentIdParam}
            onChange={(value) => onSelectStudent(value)}
          />
          <div className="month-pagination">
            <button
              type="button"
              className="month-pagination__button"
              onClick={onPrevYear}
              disabled={!canGoToPrevYear}
            >
              <ChevronLeft />
            </button>
            <button
              type="button"
              className="month-pagination__button"
              onClick={onNextYear}
              disabled={!canGoToNextYear}
            >
              <ChevronRight />
            </button>
            <span>
              {t('TAXES.TABLE.HEADERS.SCHOOL_YEAR')}: {selectedYear} -{' '}
              {selectedYear ? selectedYear + 1 : ''}
            </span>
          </div>
        </div>
        {selectedStudent &&
          installmentsData?.result &&
          installmentsData.result.length > 0 && (
            <div className="school-taxes__filter">
              <div className="school-taxes__filter__info">
                <div className="school-taxes__filter__row">
                  <div className="school-taxes__filter__item">
                    <span className="school-taxes__filter__label">
                      {t('PAYMENTS.TABLE.HEADERS.TOTAL_TAX')}:
                    </span>
                    <span className="school-taxes__filter__value">
                      {displayPrice(
                        selectedStudent.totalAmount,
                        t('PAYMENTS.LABELS.BGN'),
                        t('PAYMENTS.LABELS.EUR'),
                        selectedStudent.currency
                      )}
                    </span>
                  </div>
                  <div className="school-taxes__filter__item">
                    <span className="school-taxes__filter__label">
                      {t('PAYMENTS.TABLE.HEADERS.PAID_AMOUNT')}:
                    </span>
                    <span className="school-taxes__filter__value">
                      {displayPrice(
                        selectedStudent.paidAmount,
                        t('PAYMENTS.LABELS.BGN'),
                        t('PAYMENTS.LABELS.EUR'),
                        selectedStudent.currency
                      )}
                    </span>
                  </div>
                  <div className="school-taxes__filter__item">
                    <span className="school-taxes__filter__label">
                      {t('PAYMENTS.TABLE.HEADERS.PENDING_AMOUNT')}:
                    </span>
                    <span className="school-taxes__filter__value">
                      {displayPrice(
                        selectedStudent.remainingAmount,
                        t('PAYMENTS.LABELS.BGN'),
                        t('PAYMENTS.LABELS.EUR'),
                        selectedStudent.currency
                      )}
                    </span>
                  </div>
                </div>
                <div className="school-taxes__filter__row school-taxes__filter__row--full">
                  <div className="school-taxes__filter__item">
                    <span className="school-taxes__filter__label">
                      {t('PAYMENTS.TABLE.HEADERS.NOTES')}:
                    </span>
                    <span className="school-taxes__filter__value">
                      {isOnSchoolTaxesScholarship(
                        selectedStudent.scholarshipType
                      ) ? (
                        t('PAYMENTS.TABLE.LABELS.ON_SCHOLARSHIP')
                      ) : selectedStudent.totalAmount ===
                        selectedStudent.paidAmount ? (
                        <span className="paid-tax">
                          <Checkmark size={'16'} />{' '}
                          {t('PAYMENTS.TABLE.LABELS.TAX_PAID')}
                        </span>
                      ) : !selectedStudent.canPay ? (
                        <span className="overdue-tax">
                          {t('TAXES.MESSAGES.OVERDUE_PAYMENTS', {
                            schoolYear:
                              selectedYear === currentSchoolYear
                                ? `${selectedYear + 1} - ${selectedYear + 2}`
                                : `${selectedYear - 1} - ${selectedYear}`,
                          })}
                        </span>
                      ) : selectedStudent.remainingInstallments &&
                        selectedStudent.remainingAmount ? (
                        t('PAYMENTS.TABLE.LABELS.REVIEW_PAYMENTS')
                      ) : (
                        '-'
                      )}
                    </span>
                  </div>
                </div>
              </div>
              {canMakePayments &&
                selectedStudent.canPay &&
                !isOnSchoolTaxesScholarship(selectedStudent.scholarshipType) &&
                selectedStudent.totalAmount !== selectedStudent.paidAmount &&
                selectedStudent.remainingAmount &&
                selectedStudent.remainingInstallments > 0 && (
                  <div className="school-taxes__filter__actions">
                    <Button onClick={onOpenPartialPayment}>
                      {t('PAYMENTS.TABLE.ACTIONS.PAY_PARTIALLY')}
                    </Button>
                    <Button onClick={onNavigateToFullPayment}>
                      {t('PAYMENTS.TABLE.ACTIONS.PAY_FULL')}
                    </Button>
                  </div>
                )}
            </div>
          )}

        {(isLoadingPricePlan || isLoadingInstallments) && <TableSkeleton />}
        {!isLoadingPricePlan && pricePlanData?.length === 0 && (
          <EmptyState message={t('TAXES.MESSAGES.NO_STUDENTS_FOR_YEAR')} />
        )}
        {!isLoadingPricePlan && !isLoadingInstallments && selectedStudent && (
          <>
            {tableChildren.length > 0 ? (
              <div className="school-taxes__installments-table">
                <Table headers={headers} rows={tableChildren} />
              </div>
            ) : (
              <EmptyState
                message={t('TAXES.MESSAGES.NO_INSTALLMENTS_FOR_YEAR')}
              />
            )}
          </>
        )}
      </div>
    </FormProvider>
  );
};
